'use client';

/**
 * AddToTripNight — on a campsite page: "Add to your trip as Night 2 · Halls
 * Gap". Reads the trip being planned on the explorer (tripNightShare) and
 * hands the pick back to it. With no trip in progress it's the plain "Plan a
 * road trip through here" link it replaced.
 */

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { withBase } from '../lib/brand';
import {
  readTripSummary,
  writePendingAdd,
  type SharedNightOption,
  type SharedTripSummary,
} from '../utils/tripNightShare';
import { suggestedSlotLabel } from '../utils/nightPlanner';

interface Props {
  entityId: number;
  name: string;
  lat?: number | string | null;
  lng?: number | string | null;
}

const toNum = (v: number | string | null | undefined): number | undefined => {
  const n = typeof v === 'string' ? parseFloat(v) : v ?? undefined;
  return typeof n === 'number' && Number.isFinite(n) ? n : undefined;
};

const nightLabel = (n: SharedNightOption) =>
  n.nights > 1 ? `Nights ${n.night}–${n.night + n.nights - 1}` : `Night ${n.night}`;

export default function AddToTripNight({ entityId, name, lat, lng }: Props) {
  const [trip, setTrip] = useState<SharedTripSummary | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    setTrip(readTripSummary());
  }, []);

  const plannerHref = withBase('/?plan=1');

  if (!trip || trip.nights.length === 0) {
    return (
      <Link href={plannerHref} className="cd-link text-sm">
        Plan a road trip through here →
      </Link>
    );
  }

  const choose = (night: SharedNightOption | null) => {
    writePendingAdd({
      createdAt: Date.now(),
      entity: { id: entityId, name, lat: toNum(lat), lng: toNum(lng) },
      night,
    });
    setDone(night ? `${nightLabel(night)} · ${night.suggested ? suggestedSlotLabel(night.name).toLowerCase() : night.name}` : 'an extra stop');
  };

  return (
    <div data-testid="add-to-trip-night">
      <p className="text-[13px] font-semibold text-slate-800">Add to your trip</p>
      <p className="text-[12px] text-slate-500">{trip.title}</p>
      {done ? (
        <div className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-800">
          Added as {done}.{' '}
          <Link href={plannerHref} className="font-semibold underline underline-offset-2">
            Open your trip →
          </Link>
        </div>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {trip.nights.map((n) => {
            const already = n.entityId === entityId;
            return (
              <li key={n.key}>
                <button
                  type="button"
                  disabled={already}
                  onClick={() => choose(n)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2 text-left text-[12.5px] hover:border-amber-400 hover:bg-amber-50 disabled:cursor-default disabled:opacity-60"
                >
                  <span className="min-w-0">
                    <span className="font-semibold text-slate-800">{nightLabel(n)}</span>
                    {n.date && (
                      <span className="ml-1 text-slate-500">
                        {new Date(`${n.date}T12:00:00`).toLocaleDateString('en-AU', {
                          weekday: 'short',
                          day: 'numeric',
                          month: 'short',
                        })}
                      </span>
                    )}
                    <span className="block truncate text-[11px] text-slate-500">
                      {n.suggested ? suggestedSlotLabel(n.name) : n.name}
                    </span>
                  </span>
                  <span className="flex-none text-[11px] font-semibold text-amber-700">
                    {already ? '✓ On your trip' : 'Stay here'}
                  </span>
                </button>
              </li>
            );
          })}
          <li>
            <button
              type="button"
              onClick={() => choose(null)}
              className="text-[12px] font-medium text-sky-700 hover:underline"
            >
              Or add it as an extra stop
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
